import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { usePlayer } from "../context/PlayerContext";
import { useSettings } from "../context/SettingsContext";
import { releaseSchema, breadcrumbSchema } from "../lib/schema";
import Seo from "../components/Seo";
import DSPButtons from "../components/DSPButtons";
import ShareButton from "../components/ShareButton";
import FollowFavorite from "../components/FollowFavorite";
import FollowStrip from "../components/FollowStrip";
import RelatedReleases from "../components/RelatedReleases";
import { VideoCard } from "../components/VideosSection";
import EmailCapture from "../components/EmailCapture";
import Footer from "../components/Footer";
import NotFound from "./NotFound";

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function ReleaseDetail() {
  const { slug } = useParams();
  const { playRelease } = usePlayer();
  const settings = useSettings();
  const [release, setRelease] = useState(null);
  const [videos, setVideos] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setVideos([]);
    api.get(`/releases/${slug}`).then((res) => {
      setRelease(res.data);
      track("release_view", { releaseId: res.data.internalId });
      api.get(`/videos?releaseId=${res.data.internalId}`).then((v) => setVideos(v.data || [])).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFound />;
  if (!release) return <div className="min-h-screen bg-ink" />;

  const isAlbum = release.releaseType === "album" || release.releaseType === "ep";
  const typeLabel = release.releaseType === "ep" ? "EP" : release.releaseType;
  const year = release.releaseDate?.slice(0, 4);
  const seoTitle = `${release.title} — ${isAlbum ? typeLabel.toUpperCase() : "Single"}${year ? ` (${year})` : ""}`;
  const seoDesc = release.seoDescription || release.description ||
    `${release.title} by DJ Código — Latin urban ${isAlbum ? typeLabel : "single"} blending reggaetón, hip-hop and Caribbean club music. Stream on Spotify, Apple Music and YouTube.`;

  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo
        title={seoTitle}
        description={seoDesc}
        image={release.socialImage || release.artwork}
        type={isAlbum ? "music.album" : "music.song"}
        path={`/music/${release.slug}`}
        jsonLd={[releaseSchema(release, settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Music", path: "/#latest-releases" }, { name: release.title, path: `/music/${release.slug}` }], settings)]}
      />
      <div className="px-6 pt-24 sm:px-10 lg:px-16">
        <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/50">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1.5 transition-colors hover:text-gold"><ArrowLeft size={14} /> Home</Link>
          <ChevronRight size={12} />
          <Link to="/#latest-releases" className="transition-colors hover:text-gold">Music</Link>
          <ChevronRight size={12} />
          <span className="truncate text-white/80">{release.title}</span>
        </nav>

        <div className="mx-auto grid max-w-6xl items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="relative">
            <div className="absolute -inset-6 rounded-full bg-gold/15 blur-3xl" />
            <div className="relative aspect-square overflow-hidden rounded-3xl border border-white/10">
              <img src={release.artwork} alt={`${release.title} by DJ Código — ${typeLabel} cover art`} className="h-full w-full object-cover" fetchPriority="high" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <div className="mb-4 flex items-center gap-3">
              {release.isNew && <span className="rounded-full bg-gold px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-black">New</span>}
              <span className="text-[11px] font-semibold uppercase tracking-widest text-white/40">{typeLabel}{release.genre ? ` • ${release.genre}` : ""}</span>
            </div>
            <h1 className="font-display text-4xl font-bold uppercase leading-none tracking-tight text-white sm:text-6xl">{release.title}</h1>
            <p className="mt-3 text-lg text-gold">DJ Código</p>
            <p className="mt-1 text-sm text-white/40">{fmt(release.releaseDate)}</p>
            {release.description && <p className="mt-5 max-w-md text-sm leading-relaxed text-white/60">{release.description}</p>}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="detail-play" onClick={() => playRelease(release)} className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-wide text-black transition-transform active:scale-95 hover:bg-gold-2">
                <Play size={18} fill="currentColor" /> Listen Now
              </button>
              <ShareButton release={release} />
            </div>
            <div className="mt-6"><DSPButtons release={release} /></div>
            <FollowStrip settings={settings} placement="release" className="mt-8" />
          </motion.div>
        </div>
      </div>

      {release.story && (
        <section data-testid="release-story" className="px-6 pt-16 sm:px-10 lg:px-16">
          <div className="mx-auto max-w-3xl">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Behind the song</p>
            <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-4xl">The story of {release.title}</h2>
            {release.story.split(/\n\s*\n/).map((p, i) => <p key={i} className="mb-4 text-base leading-relaxed text-white/70">{p}</p>)}
          </div>
        </section>
      )}

      {videos.length > 0 && (
        <section data-testid="release-videos" className="px-6 pt-16 sm:px-10 lg:px-16">
          <div className="mx-auto max-w-6xl">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Watch</p>
            <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-4xl">{release.title} on video</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{videos.slice(0, 3).map((v, i) => <VideoCard key={v.youtubeId} video={v} index={i} />)}</div>
          </div>
        </section>
      )}

      {release.lyrics && (
        <section data-testid="release-lyrics" className="px-6 pt-16 sm:px-10 lg:px-16">
          <div className="mx-auto max-w-3xl">
            <details className="group rounded-3xl border border-white/10 bg-surface/60 p-6">
              <summary className="cursor-pointer list-none font-display text-xl font-bold uppercase tracking-tight text-white">Lyrics <span className="ml-2 text-xs font-normal text-white/40 group-open:hidden">— tap to read</span></summary>
              <pre className="mt-5 whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/70">{release.lyrics}</pre>
            </details>
          </div>
        </section>
      )}

      <FollowFavorite
        settings={settings}
        title="Like What You Hear?"
        subtitle="Don't miss the next release. Follow DJ Código on Spotify and catch every new drop."
      />

      {isAlbum && release.tracks?.length > 0 && (
        <div className="px-6 sm:px-10 lg:px-16">
          <div className="mx-auto mt-4 max-w-6xl">
            <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">Tracklist</h2>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              {release.tracks.map((t, i) => (
                <button
                  key={i}
                  data-testid={`track-${i}`}
                  onClick={() => playRelease(release, t)}
                  className="flex w-full items-center gap-4 border-b border-white/5 px-5 py-4 text-left transition-colors last:border-0 hover:bg-white/[0.03]"
                >
                  <span className="w-6 font-display text-sm text-white/40">{t.trackNumber || i + 1}</span>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 text-gold">
                    <Play size={15} fill="currentColor" className="ml-0.5" />
                  </span>
                  <span className="flex-1 truncate font-medium text-white">{t.title}</span>
                  {t.durationMs && <span className="text-xs text-white/30">{Math.floor(t.durationMs / 60000)}:{String(Math.floor((t.durationMs % 60000) / 1000)).padStart(2, "0")}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <RelatedReleases current={release} />
      <EmailCapture source={`release:${release.slug}`} releaseId={release.internalId} />
      <Footer settings={settings} />
    </div>
  );
}
